import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaPhone, FaClock, FaArrowLeft, FaSpinner, FaExclamationTriangle, FaSearch } from 'react-icons/fa';
import api from '../api';
import './StoreSelection.css';

export default function StoreSelection() {
  const [location, setLocation] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('requesting');
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualLocation, setManualLocation] = useState({
    latitude: '',
    longitude: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      setLocationPermission('denied');
      setLoading(false);
      return;
    }

    setLocationPermission('requesting');
    setError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('Location obtained:', position.coords);
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        setLocationPermission('granted');
        fetchNearbyStores(latitude, longitude);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to access your location. ';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'Please enable location services.';
            break;
        }
        
        setError(errorMessage);
        setLocationPermission('denied');
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const fetchNearbyStores = async (latitude, longitude) => {
    try {
      setLoading(true);
      const response = await api.post('/findNearbyStores', {
        latitude,
        longitude
      });

      if (response.data?.status?.statusCode === 'code-200') {
        const storesData = response.data.data || [];
        setStores(storesData);
        setError(null); // Clear any previous errors
      } else {
        setError('No stores found nearby. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      setError('Failed to load nearby stores. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSelect = (store) => {
    navigate('/order/place', { 
      state: { 
        selectedStore: store,
        userLocation: location 
      } 
    });
  };

  const handleRetry = () => {
    setError(null);
    getCurrentLocation();
  };

  const handleManualLocation = () => {
    setShowManualInput(true);
  };

  const handleManualLocationSubmit = (e) => {
    e.preventDefault();
    const lat = parseFloat(manualLocation.latitude);
    const lng = parseFloat(manualLocation.longitude);
    
    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid latitude and longitude values');
      return;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert('Please enter valid coordinate ranges (Latitude: -90 to 90, Longitude: -180 to 180)');
      return;
    }
    
    setLocation({ latitude: lat, longitude: lng });
    setLocationPermission('granted');
    setShowManualInput(false);
    fetchNearbyStores(lat, lng);
  };

  const handleManualLocationChange = (e) => {
    const { name, value } = e.target;
    setManualLocation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="store-selection-page">
      <div className="store-selection-container">
        {/* Header */}
        <div className="page-header">
          <button 
            className="back-btn"
            onClick={() => navigate('/')}
            aria-label="Go back to home"
          >
            <FaArrowLeft />
          </button>
          <h1>Find Nearby Stores</h1>
          <p>Select a store to place your order</p>
        </div>

        {/* Location Status */}
        <div className="location-status">
          {locationPermission === 'requesting' && (
            <div className="status-card requesting">
              <FaSpinner className="spinning" />
              <span>Getting your location...</span>
            </div>
          )}

          {locationPermission === 'granted' && location && (
            <div className="status-card success">
              <FaMapMarkerAlt />
              <span>Location found! Showing stores within 5km</span>
            </div>
          )}

          {locationPermission === 'denied' && (
            <div className="status-card error">
              <FaExclamationTriangle />
              <span>Location access denied</span>
              <div className="error-actions">
                <button onClick={handleRetry} className="retry-btn">
                  Try Again
                </button>
                <button onClick={handleManualLocation} className="manual-btn">
                  Enter Location Manually
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Manual Location Input */}
        {showManualInput && (
          <div className="manual-location-form">
            <h3>Enter Your Location Coordinates</h3>
            <p>Please enter your latitude and longitude coordinates to find nearby stores.</p>
            <form onSubmit={handleManualLocationSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="latitude">Latitude</label>
                  <input
                    type="number"
                    id="latitude"
                    name="latitude"
                    value={manualLocation.latitude}
                    onChange={handleManualLocationChange}
                    placeholder="e.g., 28.6139"
                    step="any"
                    min="-90"
                    max="90"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="longitude">Longitude</label>
                  <input
                    type="number"
                    id="longitude"
                    name="longitude"
                    value={manualLocation.longitude}
                    onChange={handleManualLocationChange}
                    placeholder="e.g., 77.2090"
                    step="any"
                    min="-180"
                    max="180"
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="search-btn">
                  <FaSearch />
                  Find Stores
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowManualInput(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <FaSpinner className="spinning large" />
            <p>Finding nearby stores...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && stores.length === 0 && (
          <div className="error-container">
            <FaExclamationTriangle className="error-icon" />
            <h3>Oops! Something went wrong</h3>
            <p>{error}</p>
            <button onClick={handleRetry} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Stores List */}
        {!loading && stores.length > 0 && (
          <div className="stores-container">
            <h2>Available Stores ({stores.length})</h2>
            <div className="stores-grid">
              {stores.map((store) => (
                <div 
                  key={store.id} 
                  className="store-card"
                  onClick={() => handleStoreSelect(store)}
                >
                  <div className="store-header">
                    <div className="store-icon">
                      <FaMapMarkerAlt />
                    </div>
                    <div className="store-info">
                      <h3>{store.store_name}</h3>
                      <p className="distance">{store.distance_km} km away</p>
                    </div>
                  </div>
                  
                  <div className="store-details">
                    <div className="detail-item">
                      <FaMapMarkerAlt />
                      <span>{store.address}</span>
                    </div>
                    <div className="detail-item">
                      <FaClock />
                      <span>Open Now</span>
                    </div>
                  </div>

                  <div className="store-actions">
                    <button className="select-store-btn">
                      Select This Store
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Stores Found */}
        {!loading && !error && stores.length === 0 && locationPermission === 'granted' && (
          <div className="no-stores-container">
            <FaMapMarkerAlt className="no-stores-icon" />
            <h3>No stores found nearby</h3>
            <p>We don't have any stores within 5km of your location.</p>
            <button onClick={handleManualLocation} className="contact-btn">
              Contact Us for Service
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
